import { get } from 'utils/request';
import { networkActions, SectionInfo } from 'actions/network';
import {
  cancellableMapPool,
  isExportAbortError,
  throwIfExportCancelled,
} from 'utils/shapefileExportCancellation';
import { EXPORT_DETAIL_FETCH_CONCURRENCY } from 'utils/shapefileExportConstants';

/* Parallel `getSectionDetails` requests during export. */
const SECTION_DETAILS_CONCURRENCY = EXPORT_DETAIL_FETCH_CONCURRENCY;

const SECTION_LABEL_KEYS = [
  'name',
  'Name',
  'sectionLabel',
  'SectionLabel',
  'SECTION_LABEL',
] as const;

/* Fields aligned with `SectionInfo` (excluding geometry WKT). */
const NETWORK_SECTION_EXPORT_ATTRIBUTE_NAMES: readonly string[] = [
  'sectionLabel',
  'description',
  'length',
  'startNode',
  'endNode',
  'roadName',
  'roadNumber',
  'roadHierarchy',
  'roadClass',
  'roadType',
  'lanes',
  'width',
  'speedLimit',
  'environment',
  'district',
  'region',
  'ward',
  'startDate',
  'dotCode',
  'adopted',
  'cogpHierarchy',
  'usrn',
];

export interface MapFeatureLike {
  geometry: unknown;
  attributes: Record<string, unknown>;
}

export function isNetworkSectionsGeoLayer(layerId: string): boolean {
  return layerId === 'network-layer' || layerId === 'direction-layer';
}

function getSectionLabelFromProps(
  attrs: Record<string, unknown> | null | undefined,
): string | null {
  if (!attrs) return null;

  for (const k of SECTION_LABEL_KEYS) {
    const v = attrs[k];
    if (v != null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }

  const lowerKeyToValue = new Map<string, unknown>();
  for (const [key, value] of Object.entries(attrs)) {
    lowerKeyToValue.set(key.toLowerCase(), value);
  }
  for (const k of SECTION_LABEL_KEYS) {
    const v = lowerKeyToValue.get(k.toLowerCase());
    if (v != null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }

  return null;
}

export function getNetworkExportProgressTotal(
  features: readonly MapFeatureLike[],
): number {
  const sectionCount = countUniqueNetworkSectionLabels(features);
  return sectionCount > 0 ? sectionCount : features.length;
}

function countUniqueNetworkSectionLabels(
  features: readonly MapFeatureLike[],
): number {
  const labels = new Set<string>();
  for (const f of features) {
    const label = getSectionLabelFromProps(f.attributes);
    if (label) labels.add(label);
  }
  return labels.size;
}

function normalizeLabel(label: string): string {
  return label.trim().toLowerCase();
}

function sectionInfoToExportAttributes(
  info: SectionInfo,
): Record<string, unknown> {
  const { geometry: _omit, ...rest } = info as SectionInfo & {
    geometry?: string;
  };
  return { ...rest };
}

function geoPropertiesLookComplete(
  props: Record<string, unknown> | null | undefined,
): boolean {
  if (!props) return false;
  const keys = Object.keys(props).filter((k) => !k.startsWith('__'));
  if (keys.length < 8) return false;
  return (
    'roadName' in props ||
    'sectionLabel' in props ||
    'description' in props ||
    'startNode' in props
  );
}

function buildGeoAttributesQuery(
  attributeNames: readonly string[],
): string {
  const params = attributeNames.map((name) => `attributes=${encodeURIComponent(name)}`);
  return params.join('&');
}

async function fetchGeoJsonAttributeLookup(
  attributeNames: readonly string[],
  totalSections: number,
  onProgress: (message: string) => void,
  signal?: AbortSignal,
): Promise<Map<string, Record<string, unknown>>> {
  onProgress(`Fetching attributes (0/${totalSections})…`);

  const attempts = [
    `network/sections/geo?includeSectionDetails=true`,
    `network/sections/geo?${buildGeoAttributesQuery(attributeNames)}`,
  ];

  for (const path of attempts) {
    throwIfExportCancelled(signal);
    try {
      const response = await get<{
        features?: Array<{ properties?: Record<string, unknown> }>;
      }>(path, { signal });
      const features = response.data?.features;
      if (!features?.length) continue;

      const first = features[0]?.properties;
      if (!geoPropertiesLookComplete(first || null)) continue;

      const map = new Map<string, Record<string, unknown>>();
      for (const f of features) {
        const props = f.properties;
        if (!props) continue;
        const label = getSectionLabelFromProps(props);
        if (!label) continue;
        const { geometry: _g, ...rest } = props as Record<string, unknown> & {
          geometry?: unknown;
        };
        map.set(normalizeLabel(label), { ...rest });
      }
      if (map.size > 0) {
        onProgress(`Fetching attributes (${map.size}/${totalSections})…`);
        return map;
      }
    } catch (error) {
      if (isExportAbortError(error, signal)) {
        throw error;
      }
      // Try next strategy or fall back to per-section details.
    }
  }

  return new Map();
}

const sectionDetailCache = new Map<string, Record<string, unknown>>();

async function loadSectionDetailsForLabels(
  labels: readonly string[],
  totalSections: number,
  completedBefore: number,
  onProgress: (message: string) => void,
  signal?: AbortSignal,
): Promise<Map<string, Record<string, unknown>>> {
  const out = new Map<string, Record<string, unknown>>();
  if (labels.length === 0) return out;

  const labelsToFetch: string[] = [];
  for (const label of labels) {
    throwIfExportCancelled(signal);
    const key = normalizeLabel(label);
    const cached = sectionDetailCache.get(key);
    if (cached) {
      out.set(key, cached);
    } else {
      labelsToFetch.push(label);
    }
  }

  if (labelsToFetch.length === 0) {
    return out;
  }

  const net = networkActions();
  const pairs = await cancellableMapPool(
    labelsToFetch,
    SECTION_DETAILS_CONCURRENCY,
    async (label) => {
      throwIfExportCancelled(signal);
      try {
        const detail = await net.getSectionDetails(label, false, signal);
        return { label, detail };
      } catch (error) {
        if (isExportAbortError(error, signal)) {
          throw error;
        }
        return { label, detail: null as SectionInfo | null };
      }
    },
    {
      signal,
      onProgress: (done) => {
        onProgress(
          `Fetching attributes (${completedBefore + done}/${totalSections})…`,
        );
      },
    },
  );

  for (const { label, detail } of pairs) {
    if (!detail) continue;
    const attrs = sectionInfoToExportAttributes(detail);
    const key = normalizeLabel(label);
    sectionDetailCache.set(key, attrs);
    out.set(key, attrs);
  }

  return out;
}

/**
 * Enriches network layer features with full section attributes (same fields as the Attributes modal).
 * Tries bulk geo GET first; falls back to batched POST network/sections per unique section label.
 */
export async function enrichNetworkFeaturesForShapefileExport(
  features: MapFeatureLike[],
  onProgress: (message: string) => void,
  signal?: AbortSignal,
): Promise<MapFeatureLike[]> {
  throwIfExportCancelled(signal);
  if (features.length === 0) return features;

  const uniqueLabels = new Set<string>();
  for (const f of features) {
    throwIfExportCancelled(signal);
    const label = getSectionLabelFromProps(f.attributes);
    if (label) uniqueLabels.add(label);
  }

  const totalSections = getNetworkExportProgressTotal(features);
  onProgress(`Fetching attributes (0/${totalSections})…`);

  const geoLookup = await fetchGeoJsonAttributeLookup(
    NETWORK_SECTION_EXPORT_ATTRIBUTE_NAMES,
    totalSections,
    onProgress,
    signal,
  );

  throwIfExportCancelled(signal);

  const labelsNeedingPost =
    uniqueLabels.size > 0
      ? [...uniqueLabels].filter((label) => {
          const fromGeo = geoLookup.get(normalizeLabel(label));
          return !fromGeo || !geoPropertiesLookComplete(fromGeo);
        })
      : [];

  const completedFromGeo =
    uniqueLabels.size > 0 ? uniqueLabels.size - labelsNeedingPost.length : 0;
  if (labelsNeedingPost.length === 0) {
    onProgress(`Fetching attributes (${totalSections}/${totalSections})…`);
  } else if (completedFromGeo > 0) {
    onProgress(`Fetching attributes (${completedFromGeo}/${totalSections})…`);
  }

  let detailByLabel = new Map<string, Record<string, unknown>>();
  if (labelsNeedingPost.length > 0) {
    detailByLabel = await loadSectionDetailsForLabels(
      labelsNeedingPost,
      totalSections,
      completedFromGeo,
      onProgress,
      signal,
    );
    onProgress(`Fetching attributes (${totalSections}/${totalSections})…`);
  }

  onProgress('Building shapefile records…');

  let processed = 0;
  return features.map((f) => {
    processed += 1;
    throwIfExportCancelled(signal);
    if (
      uniqueLabels.size === 0 &&
      (processed === 1 ||
        processed === features.length ||
        processed % 250 === 0)
    ) {
      onProgress(`Fetching attributes (${processed}/${totalSections})…`);
    }

    const base = { ...f.attributes };
    const label = getSectionLabelFromProps(base);
    const key = label ? normalizeLabel(label) : '';
    const fromGeo = key ? geoLookup.get(key) : undefined;
    const fromPost = key ? detailByLabel.get(key) : undefined;

    return {
      geometry: f.geometry,
      attributes: {
        ...(fromGeo || {}),
        ...(fromPost || {}),
        ...base,
      },
    };
  });
}
